/**
 * Downloading a file the server built.
 *
 * Three things every existing download in this app gets wrong, fixed here once:
 *
 * 1. **RTK Query has no 'blob' response handler.** The union is
 *    `'content-type' | 'json' | 'text' | (response) => Promise<any>`, and anything
 *    unrecognised falls through to `response.text()`. A CSV survives that; an
 *    .xlsx or .pdf is decoded as UTF-8, invalid bytes become U+FFFD, and the file
 *    will not open. Endpoints must pass the function form — see `exportCommsDevices`.
 * 2. **The object URL is never revoked**, so every download leaks its blob for the
 *    life of the tab.
 * 3. **The server's filename is discarded** and one is invented client-side.
 */

/**
 * Read the filename out of a Content-Disposition header.
 *
 * Prefers RFC 5987 (`filename*=UTF-8''…`) over the plain form, strips quotes, and
 * refuses anything path-like so a malformed header cannot suggest a directory.
 *
 * @param {string|null|undefined} header
 * @returns {string|null} null when there is nothing usable to use.
 */
export const parseContentDispositionFilename = (header) => {
  if (!header) return null;

  let name = null;
  const extended = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (extended) {
    try {
      name = decodeURIComponent(extended[1]);
    } catch {
      name = extended[1];
    }
  }
  if (!name) {
    const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(header);
    if (plain) name = (plain[2] ?? plain[1]).trim();
  }
  if (!name) return null;

  // Keep the last segment only, and never a dotfile: a filename is a name, not a path.
  const base = name.split(/[\\/]/).pop().replace(/^\.+/, '').trim();
  return base || null;
};

/**
 * Save a blob to disk as `filename`.
 *
 * The object URL is revoked in `finally`, so a throwing click still cleans up.
 */
export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.rel = 'noopener';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    window.URL.revokeObjectURL(url);
  }
};

/**
 * Turn whatever an export rejection carries into one sentence for a toast.
 *
 * RTK Query rejects with `{status, data}` rather than an Error, and `data` can be
 * a string, a parsed object, or a Blob when the handler asked for one. An aborted
 * request returns null: the user cancelled, so say nothing.
 *
 * @returns {Promise<string|null>}
 */
export const describeExportError = async (error) => {
  if (!error) return 'Export failed.';
  if (error.name === 'AbortError') return null;
  if (error.status === 'FETCH_ERROR') return 'Could not reach the server. Check your connection.';

  let data = error.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    data = await data.text();
  }
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return data.trim() || `Export failed (HTTP ${error.status}).`;
    }
  }
  if (data && typeof data === 'object') {
    if (data.detail) return String(data.detail);
    const flattened = Object.values(data).flat().filter(Boolean).join(' ');
    if (flattened) return flattened;
  }
  return `Export failed${error.status ? ` (HTTP ${error.status})` : ''}.`;
};
